import type { Group, Student, Settings, ScoringWeights } from "../types/models";

interface ClassStats {
    meanSkill: number;
}

/** Default scoring weights */
export const DEFAULT_WEIGHTS: ScoringWeights = {
    size: 0.50,
    skill: 0.25,
    tags: 0.25
};

/**
 * Get effective weights, normalizing to sum to 1.0
 */
export function getEffectiveWeights(settings: Settings): ScoringWeights {
    const weights = settings.scoringWeights ?? DEFAULT_WEIGHTS;
    const total = weights.size + weights.skill + weights.tags;
    if (total === 0) return DEFAULT_WEIGHTS;
    return {
        size: weights.size / total,
        skill: weights.skill / total,
        tags: weights.tags / total
    };
}

/**
 * Calculate how well a student fits in a group
 * Supports configurable weights via settings.scoringWeights
 */
export function scoreCandidate(
    student: Student,
    groupMembers: Student[],
    classStats: ClassStats,
    settings: Settings,
    targetSize?: number
): number {
    const weights = getEffectiveWeights(settings);

    // 1. SIZE SCORE - Asymmetric penalty prioritizing target size
    let sizeScore = 0.5;
    if (targetSize !== undefined) {
        const newSize = groupMembers.length + 1;

        if (newSize <= targetSize) {
            // UNDERFILLED: Gentle exponential decay (base 0.8)
            // Encourages filling groups toward target size
            const distance = targetSize - newSize;
            sizeScore = Math.pow(0.8, distance);
        } else {
            // OVERFILLED: Steep exponential decay (base 0.5)
            // Strongly discourages exceeding target size
            const distance = newSize - targetSize;
            sizeScore = Math.pow(0.5, distance);
        }
    }

    // 2. SKILL SCORE - How close to class mean
    let skillScore = 0.5; // neutral default
    if (settings.balanceSkill) {
        const groupWithStudent = [...groupMembers, student];
        const groupMean = groupWithStudent.reduce((sum, s) => sum + s.skill, 0) / groupWithStudent.length;
        const deviation = Math.abs(groupMean - classStats.meanSkill);
        // Map deviation to score: 0 deviation = 1.0, 10 deviation = 0.0
        skillScore = Math.max(0, 1 - (deviation / 10));
    }

    // 3. TAG SCORE - Diversity or grouping based on tag behavior
    const tagScore = calculateTagScore(student, groupMembers, settings.tagBehavior);

    // Final weighted score using configurable weights
    return (weights.size * sizeScore) + (weights.skill * skillScore) + (weights.tags * tagScore);
}

/**
 * Calculate tag score based on tag behavior settings
 */
function calculateTagScore(
    student: Student,
    groupMembers: Student[],
    tagBehavior: Map<string, 'balance' | 'group'>
): number {
    if (student.tags.length === 0) return 0.5; // neutral for students with no tags

    const scores = student.tags.map(tag => {
        const mode = tagBehavior.get(tag) ?? 'balance';

        if (mode === 'balance') {
            // Balance mode: reward diversity (fewer students with this tag in group)
            const countWithTag = groupMembers.filter(m => m.tags.includes(tag)).length;
            // Score: 1.0 if nobody has tag, 0.5 if 1 has it, 0.33 if 2 have it, etc.
            return 1 / (1 + countWithTag);
        } else {
            // Group mode: handled in tag grouping phase, neutral here
            return 0.5;
        }
    });

    // Average score across all tags
    return scores.reduce((sum, s) => sum + s, 0) / scores.length;
}

/**
 * Score an entire group (for optimization phase)
 * Returns average candidate score if each member were added last
 */
export function scoreGroup(
    group: Group,
    students: Student[],
    classStats: ClassStats,
    settings: Settings,
    targetSize?: number
): number {
    const idToStudent = new Map(students.map(s => [s.id, s]));
    const members = group.memberIds
        .map(id => idToStudent.get(id))
        .filter((s): s is Student => s !== undefined);

    if (members.length <= 1) return 0.5;

    let totalScore = 0;
    for (let i = 0; i < members.length; i++) {
        const student = members[i];
        if (!student) continue;

        const others = members.filter((_, idx) => idx !== i);
        totalScore += scoreCandidate(student, others, classStats, settings, targetSize);
    }

    return totalScore / members.length;
}
